import { useQuery } from "@tanstack/react-query";
import { fetchCommunication } from "../lib/api";

export default function Communication() {
  const { data, isLoading } = useQuery({ queryKey: ["communication"], queryFn: fetchCommunication });

  if (isLoading) return <div className="text-sm text-slate-500">Loading Communication Feed…</div>;

  const speeches = data?.speeches ?? [];
  const indices = data?.committee_indices ?? {};
  const shifts = data?.tone_shifts ?? [];

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="space-y-2">
        <h1 className="text-lg font-semibold">Central-Bank Communication</h1>
        <p className="max-w-2xl text-xs text-slate-500">
          Traceable keyword-model sentiment from FOMC + BIS speeches. Each score decomposes into the phrases that produced it. Context-blind by design — educational only.
        </p>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(indices).map(([bank, idx]) => (
          <div key={bank} className="rounded-xl border border-surface-2 bg-surface-1 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-slate-400">{bank}</div>
            <div className="mt-2 flex items-end gap-2">
              <div className="font-mono text-2xl font-semibold">{idx.days30.toFixed(1)}</div>
              <div className="pb-1 text-xs text-slate-500">30d index</div>
            </div>
            <div className="text-xs text-slate-500">90d: {idx.days90.toFixed(1)}</div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-surface-2">
              <div
                className={`h-1.5 rounded-full ${idx.days30 >= 0 ? "bg-macro-up" : "bg-macro-down"}`}
                style={{
                  width: `${Math.min(100, Math.abs(idx.days30) * 10)}%`,
                  marginLeft: idx.days30 >= 0 ? "50%" : `${50 - Math.min(50, Math.abs(idx.days30) * 5)}%`,
                }}
              />
            </div>
          </div>
        ))}
      </section>

      {shifts.length > 0 && (
        <section className="rounded-xl border border-surface-2 bg-surface-1 p-5">
          <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Tone shifts</h2>
          <div className="space-y-2">
            {shifts.map((s) => (
              <div key={`${s.bank}-${s.change}`} className="flex items-center justify-between rounded-lg border border-surface-2 bg-surface-2 px-3 py-2">
                <span className="text-sm font-medium">{s.bank}</span>
                <span className={`text-xs ${s.change > 0 ? "text-macro-up" : "text-macro-down"}`}>
                  {s.change > 0 ? "hawkish" : "dovish"} shift · {s.change.toFixed(1)}σ
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-xl border border-surface-2 bg-surface-1 p-5">
        <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Scored communications</h2>
        {speeches.length === 0 ? (
          <div className="text-sm text-slate-500">No scored communications available.</div>
        ) : (
          <ul className="space-y-3">
            {speeches.map((s, i) => (
              <li key={`${s.url}-${i}`} className="rounded-lg border border-surface-2 bg-surface-2 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-macro-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-macro-accent">{s.bank}</span>
                  <span className="text-[10px] text-slate-500">{s.date}</span>
                  <span className={`text-xs font-mono ${s.score > 0 ? "text-macro-up" : s.score < 0 ? "text-macro-down" : "text-slate-400"}`}>
                    {s.score > 0 ? "+" : ""}
                    {s.score.toFixed(1)}
                  </span>
                </div>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block text-sm font-medium text-macro-accent hover:underline"
                >
                  {s.speaker}
                </a>
                {s.phrases.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.phrases.slice(0, 5).map((p, j) => (
                      <span
                        key={`${p.text}-${j}`}
                        className={`rounded px-1.5 py-0.5 text-[10px] ${
                          p.polarity === "hawkish" ? "bg-macro-up/10 text-macro-up" : "bg-macro-down/10 text-macro-down"
                        }`}
                      >
                        {p.text}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {data?.coverage_gap && (
        <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4 text-xs text-yellow-500">
          {data.coverage_gap}
        </div>
      )}

      <div className="text-[10px] text-slate-600">
        Source: {data?.source} · {data?.updated_at ? new Date(data.updated_at).toLocaleString() : ""}
        {data?.delayed && " · delayed"} · educational
      </div>
    </div>
  );
}
